import { Component, OnInit, signal, inject, ViewChild } from '@angular/core'; // + OnInit + inject
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router'; // + Route/Router
import { Title } from '@angular/platform-browser';
import { QuestionnaireService } from '../../core/services/questionnaire.service'; // + Service

// PrimeNG 21
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MultiSelectModule } from 'primeng/multiselect';
import { FileUploadModule, FileUpload } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TooltipModule } from 'primeng/tooltip';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormDropdownService } from '../../core/services/form-dropdown.service';
import { NoSpecialCharsValidator } from '../../core/validators/no-special-chars.validator';
import { FileValidationService } from '../../core/services/file-validation.service';

@Component({
  selector: 'app-create-file-checklist',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    DialogModule,
    ToastModule,
    MultiSelectModule,
    FileUploadModule,
    TagModule,
    ConfirmDialogModule,
    DragDropModule,
    BreadcrumbModule,
    TooltipModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './create-file-checklist.html',
  styleUrls: ['./create-file-checklist.scss'],
})
export class CreateFileChecklist implements OnInit {
  form: FormGroup;
  showItemDialog = false;
  checklistId: string | null = null; // ID per la modifica
  editingItemIndex: number | null = null;
  itemDialogValidationAttempted = false;

  @ViewChild('exampleFileUpload') exampleFileUpload?: FileUpload;

  // Array per memorizzare i file effettivi di ogni item
  // Indice corrisponde alla posizione nell'array items
  private itemFiles: (File | null)[] = [];



  fileExtensions = [
    { label: 'JPG', value: 'image/jpeg' },
    { label: 'PDF', value: 'application/pdf' },
    { label: 'DOC', value: 'application/msword' },
    {
      label: 'DOCX',
      value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    { label: 'XLS', value: 'application/vnd.ms-excel' },
    {
      label: 'XLSX',
      value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  ];

  itemForm = {
    label: '',
    allowedTypes: [] as string[],
    exampleFile: null as File | null,
    hasExpirationDate: false,
    isMandatory: false,
    isTemplateFilePresent: false,
  };

  private mimeToExtMap: Record<string, string> = {
    'image/jpeg': '.jpg',
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  };

  private extToMimeMap: Record<string, string> = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.pdf': 'application/pdf',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };

  trusteeTypes = signal<Array<{ name: string; code: string }>>([]);
  breadcrumbItems = signal<MenuItem[]>([]);
  private titleService = inject(Title);

  private fileValidationService = inject(FileValidationService);

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private service: QuestionnaireService,
    private route: ActivatedRoute,
    private router: Router,
    private formDropdownService: FormDropdownService,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, NoSpecialCharsValidator('strict')]],
      trusteeTypes: [[], Validators.required],
      type: ['CHECKLIST_FILE'],
      items: this.fb.array([], Validators.minLength(1)),
    });
  }

  ngOnInit(): void {
    // WCAG 9.2.4.2: Set descriptive page title
    this.titleService.setTitle('Crea Checklist - Portale Fiduciari AXA');
    
    // Leggiamo l'ID dall'URL
    this.checklistId = this.route.snapshot.paramMap.get('id');
    
    // Set breadcrumb based on create/edit mode
    if (this.checklistId) {
      this.breadcrumbItems.set([
        { label: 'Checklist', routerLink: '/list-file-checklist' },
        { label: 'Modifica Checklist' },
      ]);
      this.titleService.setTitle('Modifica Checklist - Portale Fiduciari AXA');
    } else {
      this.breadcrumbItems.set([
        { label: 'Checklist', routerLink: '/list-file-checklist' },
        { label: 'Crea Checklist' },
      ]);
    }
    
    this.retriveTrusteeTypes();

    if (this.checklistId) {
      this.loadChecklistData(this.checklistId);
    }
  }

  private loadChecklistData(id: string) {
    this.service.getFileChecklistsById(id).subscribe({
      next: (response: any) => {
        // Estraiamo l'oggetto 'data' dal wrapper ApiResponse
        const data = response.data || response;

        if (!data) return;

        this.form.patchValue({
          name: data.name,
          trusteeTypes: data.trusteeTypes || [],
          type: data.type || 'FILE_CHECKLIST',
        });

        this.items.clear();
        this.itemFiles = [];

        // Cerchiamo 'fileUploads' (il nuovo nome dal Mapper Java)
        const checklistItems = data.fileUploads || data.fileUploadRequests || [];

        checklistItems.forEach((item: any) => {
          // Ripristiniamo i MIME types dalla stringa "allowedExtensions" (il BE usa virgola come sep)
          const allowedTypes: string[] = item.allowedExtensions
            ? item.allowedExtensions
                .split(/[,;]/)
                .map((ext: string) => this.extToMimeMap[ext.trim().toLowerCase()] || ext.trim())
                .filter(Boolean)
            : [];

          this.items.push(
            this.fb.group({
              id: [item.id || null],                    // UUID item — serve per il download template
              label: [item.text || item.label, Validators.required],
              allowedTypes: [allowedTypes],
              allowedExtensions: [item.allowedExtensions || ''],
              isTemplateFilePresent: [item.isTemplateFilePresent || false],
              exampleFileName: [item.templateFileName || item.templateFile || item.exampleFileName || null],
              hasExpirationDate: [item.hasExpirationDate || false],
              isMandatory: [item.isMandatory || false],
            }),
          );

          // Gli item esistenti non hanno file da caricare (sono già sul server)
          this.itemFiles.push(null);
        });
      },
      error: (err) => console.error('Errore caricamento edit', err),
    });
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  openItemDialog() {
    this.resetItemForm();
    this.editingItemIndex = null;
    this.itemDialogValidationAttempted = false;
    this.showItemDialog = true;
  }

  editItem(index: number) {
    const item = this.items.at(index)?.value;
    if (!item) return;

    this.itemForm = {
      label: item.label || '',
      allowedTypes: [...(item.allowedTypes || [])],
      exampleFile: null,
      hasExpirationDate: !!item.hasExpirationDate,
      isMandatory: !!item.isMandatory,
      isTemplateFilePresent: !!item.isTemplateFilePresent,
    };

    this.editingItemIndex = index;
    this.itemDialogValidationAttempted = false;
    this.showItemDialog = true;
  }

  onTemplateToggle(): void {
    // Se si disabilita il template, cancella l'eventuale file già selezionato
    if (!this.itemForm.isTemplateFilePresent) {
      this.itemForm.exampleFile = null;
      this.exampleFileUpload?.clear();
    }
  }

  onUploadExample(event: any) {
    const selectedFile = event?.files?.[0] ?? null;
    this.itemForm.exampleFile = selectedFile instanceof File ? selectedFile : null;
  }

  async onSelectExample(event: any) {
    const selectedFile: File | null = event?.files?.[0] instanceof File ? event.files[0] : null;
    if (!selectedFile) {
      this.itemForm.exampleFile = null;
      return;
    }

    // 🔒 Validazione sicurezza file: size, MIME type, extension, magic numbers
    const validation = await this.fileValidationService.validateFileChecklist(selectedFile);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 File non valido',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000,
      });
      this.itemForm.exampleFile = null;
      this.exampleFileUpload?.clear();
      return;
    }

    this.itemForm.exampleFile = selectedFile;
  }

  saveItem() {
    this.itemDialogValidationAttempted = true;

    // ✅ XSS VALIDATION: Controlla caratteri speciali nel campo label
    const specialCharsPattern = /^[^<>"'&/\\]*$/;
    if (this.itemForm.label && !specialCharsPattern.test(this.itemForm.label)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: 'Il nome documento contiene caratteri speciali non permessi (< > " \' & / \\). Correggi il campo evidenziato.',
        life: 6000,
      });
      return;
    }

    if (this.isItemLabelInvalid() || this.isAllowedTypesInvalid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Campi obbligatori',
        detail: 'Compila il nome documento e seleziona almeno un formato ammesso.',
      });
      return;
    }

    const allowedExtensions = this.itemForm.allowedTypes
      .map((mime) => this.mimeToExtMap[mime] || mime)
      .join(',');

    const existingExampleFileName =
      this.editingItemIndex !== null
        ? this.items.at(this.editingItemIndex)?.get('exampleFileName')?.value
        : null;

    const existingItemId = this.editingItemIndex !== null
      ? this.items.at(this.editingItemIndex)?.get('id')?.value
      : null;

    // isTemplateFilePresent viene settato direttamente dalla checkbox dell'utente
    const isTemplateFilePresent = this.itemForm.isTemplateFilePresent;

    // Se isTemplateFilePresent=false, il nome file non ha senso mantenerlo
    const finalExampleFileName = isTemplateFilePresent
      ? (this.itemForm.exampleFile?.name || existingExampleFileName || null)
      : null;

    const newItem = this.fb.group({
      id: [existingItemId || null],
      label: [this.itemForm.label, [Validators.required, NoSpecialCharsValidator('strict')]],
      allowedTypes: [this.itemForm.allowedTypes],
      allowedExtensions: [allowedExtensions],
      isTemplateFilePresent: [isTemplateFilePresent],
      exampleFileName: [finalExampleFileName],
      hasExpirationDate: [this.itemForm.hasExpirationDate],
      isMandatory: [this.itemForm.isMandatory],
    });

    if (this.editingItemIndex !== null) {
      this.items.setControl(this.editingItemIndex, newItem);
      this.itemFiles[this.editingItemIndex] = isTemplateFilePresent ? this.itemForm.exampleFile : null;
    } else {
      this.items.push(newItem);
      this.itemFiles.push(isTemplateFilePresent ? this.itemForm.exampleFile : null);
    }

    this.showItemDialog = false;
    this.messageService.add({
      severity: 'info',
      summary: this.editingItemIndex !== null ? 'Elemento modificato' : 'Elemento aggiunto',
      detail:
        this.editingItemIndex !== null ? 'Requisito file aggiornato' : 'Requisito file inserito',
    });

    this.resetItemForm();
    this.editingItemIndex = null;
    this.itemDialogValidationAttempted = false;
  }

  formatMime(mime: string): string {
    const found = this.fileExtensions.find((ext) => ext.value === mime);
    return found ? found.label : 'FILE';
  }

  resetItemForm() {
    this.itemForm = { label: '', allowedTypes: [], exampleFile: null, hasExpirationDate: false, isMandatory: false, isTemplateFilePresent: false };
    this.exampleFileUpload?.clear();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  chooseNewExampleFile(clearCb: (...args: any[]) => void, chooseCb: (...args: any[]) => void): void {
    clearCb();
    this.itemForm.exampleFile = null;
    chooseCb();
  }

  /**
   * Rimuove un item dalla lista e il suo file associato
   */
  removeItem(index: number): void {
    this.items.removeAt(index);
    this.itemFiles.splice(index, 1);
  }

  onItemDialogHide() {
    this.resetItemForm();
    this.editingItemIndex = null;
    this.itemDialogValidationAttempted = false;
  }

  drop(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    // Riordina gli item del FormArray
    moveItemInArray(this.items.controls, event.previousIndex, event.currentIndex);
    this.items.updateValueAndValidity();

    // Riordina anche l'array dei file
    moveItemInArray(this.itemFiles, event.previousIndex, event.currentIndex);
  }

  /**
   * Controlla se ci sono campi con caratteri speciali non permessi
   * @returns Array di nomi campi con errori
   */
  private hasSpecialCharsErrors(): string[] {
    const fieldsWithErrors: string[] = [];

    // Controlla il campo name
    const nameControl = this.form.get('name');
    if (nameControl?.hasError('specialChars')) {
      fieldsWithErrors.push('Nome Checklist');
    }

    // Controlla tutti i label nei items
    this.items.controls.forEach((item, index) => {
      const labelControl = item.get('label');
      if (labelControl?.hasError('specialChars')) {
        fieldsWithErrors.push(`Documento ${index + 1} (${labelControl.value || 'senza nome'})`);
      }
    });

    return fieldsWithErrors;
  }

  submit() {
    // ✅ XSS VALIDATION: Controlla caratteri speciali PRIMA della validazione required
    const specialCharsFields = this.hasSpecialCharsErrors();
    if (specialCharsFields.length > 0) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    if (this.form.invalid || this.items.length === 0) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Campi obbligatori',
        detail: 'Compila tutti i campi obbligatori e aggiungi almeno un documento.',
      });
      return;
    }

    // Build ContractChecklist DTO
    const formValue = this.form.value;
    const today = new Date();
    const validFrom = today.toISOString().slice(0, 10);
    const validToDate = new Date(today);
    validToDate.setFullYear(validToDate.getFullYear() + 100);
    const validTo = validToDate.toISOString().slice(0, 10);
    const trusteeTypes = formValue.trusteeTypes;

    // Map items to fileUploadRequests
    // isTemplateFilePresent viene letto direttamente dal FormGroup (settato dalla checkbox)
    const fileUploadRequests = (formValue.items || []).map((item: any, idx: number) => ({
      text: item.label,
      position: idx + 1,
      hasExpirationDate: item.hasExpirationDate || false,
      allowedExtensions: item.allowedExtensions || '',
      isMandatory: item.isMandatory || false,
      isTemplateFilePresent: !!item.isTemplateFilePresent,
    }));

    // Build payload for ContractChecklist
    const payload = {
      name: formValue.name,
      trusteeTypes,
      validFrom,
      validTo,
      fileUploadRequests,
      modifiedBy: formValue.modifiedBy || '',
    };

    const templateFiles = this.itemFiles.map(file => file || null);

    // Chiamata al service con file
    const request$ = this.checklistId
      ? this.service.updateContractChecklistWithFiles(this.checklistId, payload, templateFiles)
      : this.service.createContractChecklistWithFiles(payload, templateFiles);

    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Salvataggio completato',
          detail: this.checklistId ? 'Checklist aggiornata' : 'Checklist creata',
        });
        setTimeout(() => this.router.navigate(['/list-file-checklist']), 1500);
      },
      error: (err) => {
        // Intercettiamo il conflitto (409) o il codice di validazione specifico
        if (err.status === 409 || err.error?.code === 'QUESTIONNAIRE_VALIDATION_ERROR') {
          // Messaggio di cortesia personalizzato
          const friendlyMessage =
            'Esiste già una checklist attiva per questo tipo di fiduciario. Puoi averne solo una attiva alla volta: prova a modificare quella esistente.';

          this.confirmationService.confirm({
            header: 'Checklist già presente',
            message: friendlyMessage,
            icon: 'pi pi-info-circle', // Icona info blu/azzurra meno "aggressiva"
            acceptLabel: 'Ho capito',
            rejectVisible: false, // Nascondiamo il tasto annulla
            acceptButtonStyleClass: 'p-button-outlined p-button-info',
            accept: () => {
              // L'utente chiude il dialogo
            },
          });
        } else {
          // Errore generico per tutti gli altri casi (500, 404, ecc.)
          this.messageService.add({
            severity: 'error',
            summary: 'Errore tecnico',
            detail: 'Non è stato possibile completare il salvataggio. Riprova tra poco.',
          });
        }
      },
    });
  }

  downloadItemTemplate(item: any): void {
    const itemId = item?.value?.id;
    if (!itemId) {
      this.messageService.add({ severity: 'warn', summary: 'Download non disponibile', detail: 'ID item non trovato' });
      return;
    }
    this.service.downloadChecklistTemplate(itemId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = item?.value?.exampleFileName || 'template';
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Errore download', detail: 'Impossibile scaricare il template' }),
    });
  }

  isItemLabelInvalid(): boolean {
    return !this.itemForm.label?.trim();
  }

  isAllowedTypesInvalid(): boolean {
    return !this.itemForm.allowedTypes || this.itemForm.allowedTypes.length === 0;
  }

  retriveTrusteeTypes() {
    this.formDropdownService
      .getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY')
      .subscribe({
        next: (response) => {
          this.trusteeTypes.set(
            (response.data || []).map((item) => ({
              name: item.valueDescription,
              code: item.valueKey,
            })),
          );
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare le tipologie fiduciario',
          });
        },
      });
  }

  navigateToList(): void {
    this.router.navigate(['/list-file-checklist']);
  }

  /**
   * Restituisce il messaggio di validazione per il tooltip del pulsante Salva
   * Utilizzato per comunicare agli utenti (inclusi screen reader) i requisiti mancanti
   */
  getValidationMessage(): string {
    const missingFields: string[] = [];
    
    if (!this.form.get('name')?.value) {
      missingFields.push('Nome Checklist');
    }
    
    if (!(this.form.get('trusteeTypes')?.value || []).length) {
      missingFields.push('Tipi di Fiduciario');
    }
    
    if (this.items.length === 0) {
      missingFields.push('almeno un documento');
    }
    
    if (missingFields.length === 0) {
      return '';
    }
    
    if (missingFields.length === 1) {
      return `Pulsante disabilitato: compila il campo ${missingFields[0]} per abilitare il salvataggio`;
    }
    
    if (missingFields.length === 2) {
      return `Pulsante disabilitato: compila i campi ${missingFields[0]} e ${missingFields[1]} per abilitare il salvataggio`;
    }
    
    // Tutti e 3 i campi mancanti
    return `Pulsante disabilitato: compila i campi obbligatori (Nome Checklist, Tipo di Fiduciario) e aggiungi almeno un documento per abilitare il salvataggio`;
  }
}
